<script>
  import maplayout from "./layout_territories.txt?raw";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import { createEventDispatcher, afterUpdate } from "svelte";
  import { fipsMap } from "./fips.js";
  import { getConfig } from "$lib/utils/config.js";

  const config = getConfig()();

  /**
   * @type {Object<string, any>[]}
   */
  export let data;

  /** @type { "rect" | "hex"} */
  export let shape = "hex";

  /** @type { import("$utils/states").FeatureOptions } */
  export let featureOption = "states";

  /**
   * A color string or a function that takes a feature and returns a color string
   * @type { string | ((d: Object<string, any>) => string) } [fill = urbanColors.blue] A string or function that returns a string to use as this layers stroke color.
   */
  export let fill = urbanColors.blue;

  /**
   * A color string to use when a feature is hovered
   * @type { string | undefined } [hoverFill = undefined]
   */
  export let hoverFill = undefined;

  /**
   * Color to use for values that are NA or otherwise undefined in the color scale
   * @type { string }
   */
  export let naFill = urbanColors.gray_shade_light;

  /**
   * A color string or a function that takes a feature and returns a color string
   * @type { string | ((d: Object) => string) } [fill = urbanColors.blue] A string or function that returns a string to use as this layers stroke color.
   */
  export let stroke = urbanColors.white;

  /**
   * Optional color string for hovered feature stroke
   * @type { string | undefined }
   */
  export let hoverStroke = undefined;

  /**
   * Stroke width of each feature
   * @type { number } [strokeWidth = 0.5]
   */
  export let strokeWidth = 0.5;

  /**
   * Stroke width of each feature when hovered
   * @type { number | undefined } [strokeWidth = undefined]
   */
  export let hoverStrokeWidth = undefined;

  /**
   * ID of the feature to highlight
   * @type { string | undefined } [highlightFeature = undefined]
   */
  export let highlightFeature = undefined;

  /**
   * Optional aria role string to be applied to SVG container. By default, the SVG is hidden from the accessiblity tree. If you add an ariaRole here, any layers should also be given an ariaRole.
   * @type { string } [ariaRole = undefined]
   */
  export let ariaRole = undefined;

  /**
   * Optional aria label string to be applied to SVG container. By default, the SVG is hidden from the accessiblity tree and should include a descriptive label. If you add an ariaRole this property can be left undefined;
   * @type { string } [ariaRole = undefined]
   */
  export let ariaLabel = undefined;

  const featureFilters = {
    states: ["PR", "VI", "MP", "GU", "AS"],
    pr: ["VI", "MP", "GU", "AS"],
    territories: []
  };

  /**
   * @param { string } layoutString
   * @returns { string[][] }
   */
  function parseLayout(layoutString) {
    return layoutString.split("\n").map((row) =>
      row.split(",").map((st) => {
        if (featureFilters[featureOption].includes(st)) {
          return "  ";
        }
        return st;
      })
    );
  }

  const dispatch = createEventDispatcher();

  $: dataLookup = data
    ? data.reduce((acc, curr) => {
        const { map_id } = curr;
        return acc.set(map_id, curr);
      }, new Map())
    : undefined;

  let width = 500;
  $: mapTiles = parseLayout(maplayout);
  $: activeRows = mapTiles.filter((row) => row.some((tile) => tile.trim() !== "")).length;
  $: shapeWidth = Math.floor(width / mapTiles[0].length);
  $: shapeHeight = getHeight(shapeWidth, shape);
  $: height = getMapHeight(activeRows, shapeHeight, shape);

  /**
   * @param { number } width
   * @param { string } shape
   * @returns { number}
   */
  function getHeight(width, shape) {
    if (shape === "hex") {
      return (width / Math.sqrt(3)) * 2.1;
    }
    return width;
  }

  function getMapHeight(numRows, unitHeight, shapeType) {
    if (shapeType === "hex") {
      return numRows * (unitHeight / 2) + unitHeight * 2.25 + (hoverStrokeWidth || 0);
    }
    return numRows * unitHeight + (hoverStrokeWidth || 0);
  }

  /**
   * @param { number } width
   * @param { number }height
   * @returns { string }
   */
  function generateHex(width, height) {
    const points = [
      [width / 2, 0],
      [width, height / 4],
      [width, (height / 4) * 3],
      [width / 2, height],
      [0, (height / 4) * 3],
      [0, height / 4]
    ];
    return (
      points.reduce((acc, point, i) => {
        acc += `${i > 0 ? "L" : ""} ${point[0]},${point[1]} `;
        return acc;
      }, "M") + "Z"
    );
  }

  $: hexPoints = generateHex(shapeWidth, shapeHeight);

  /**
   * @param {number} row
   * @param {number} shapeHeight
   * @returns {number}
   */
  function getY(row, shapeHeight) {
    if (shape === "hex") {
      return row * shapeHeight * 0.75;
    } else {
      return row * shapeHeight;
    }
  }

  /**
   * @param {number} column
   * @param {number} row
   * @param {number} shapeWidth
   * @returns {number}
   */
  function getX(column, row, shapeWidth) {
    if (shape === "hex") {
      if (row % 2 !== 0) {
        return column * shapeWidth - shapeWidth * 0.5;
      } else {
        return column * shapeWidth;
      }
    } else {
      return column * shapeWidth;
    }
  }
  /**
   * @param {Object<string, any>} feature
   * @param {string|((feature: Object<string, any>) => string) } fill
   * @param {string} naFill
   */
  function getFill(feature, fill, naFill) {
    if (!feature) {
      return naFill;
    }
    if (typeof fill === "string") {
      return fill;
    }
    return fill(feature) || naFill;
  }

  /**
   * @param {string} abbr
   */
  function getFeatureData(abbr) {
    if (!dataLookup) {
      return null;
    }
    const fips = fipsMap.get(abbr);
    if (dataLookup.has(fips)) {
      return dataLookup.get(fips);
    } else {
      //if data doesn't exist for state, fill all columns with N/A values
      let fallbackData = {
        map_id: fips
      };
      config.columns.keys().forEach((col) => {
        fallbackData[col] = "N/A";
      });
      return fallbackData;
    }
  }

  // holds highlighted feature DOM element
  /** @type { Element | null} */
  let highlightFeatureNode;

  /** @type { Element } */
  let el;

  /**
   * Raise a dom node to top of siblings
   * @param { Element } el The DOM element to raise
   * @returns void
   */
  export function raise(el) {
    el.parentNode?.appendChild(el);
  }
  /**
   * @param { Event } e
   * @param { string } state
   */
  function handleMousemove(e, state) {
    if (e.target && e.target instanceof Element) {
      raise(e.target);
    }
    if (highlightFeatureNode) {
      raise(highlightFeatureNode);
    }

    const props = getFeatureData(state);
    dispatch("mousemove", { e, props });
  }

  /**
   *
   * @param { Event } e
   * @param { string } state
   */
  function handleClick(e, state) {
    if (e.target instanceof Element) {
      raise(e.target);
    }
    const props = getFeatureData(state);
    dispatch("click", { e, props });
  }

  // function handleMouseout(e) {}

  $: if (highlightFeatureNode) {
    raise(highlightFeatureNode);
  }

  function getHighlight(fips, highlight) {
    return fips === highlight;
  }

  afterUpdate(() => {
    highlightFeatureNode = el.querySelector("path.highlight");
  });
</script>

<div
  class="tile-map-wrap"
  bind:clientWidth={width}
  aria-hidden={typeof ariaRole === "undefined"}
  role={ariaRole}
  aria-label={ariaLabel}
>
  <svg bind:this={el} {width} {height} viewBox="0 0 {width} {height}">
    <rect
      role="presentation"
      fill={urbanColors.white}
      x="0"
      y="0"
      {width}
      {height}
      on:mousedown={() => dispatch("bgclick")}
    ></rect>
    <g
      class="tiles"
      style:--hover-fill={hoverFill || null}
      style:--hover-stroke={hoverStroke || null}
      style:--hover-stroke-width={hoverStrokeWidth || strokeWidth}
      class:hover-fill={hoverFill}
    >
      {#each mapTiles as row, rowIndex}
        {#each row as tile, columnIndex}
          {#if tile.trim() !== ""}
            {#if shape === "hex"}
              <path
                class="tile-shape"
                d={hexPoints}
                fill={getFill(getFeatureData(tile), fill, naFill)}
                transform="translate({getX(columnIndex, rowIndex, shapeWidth)}, {getY(
                  rowIndex,
                  shapeHeight
                )})"
                stroke="white"
                role="presentation"
                class:highlight={getHighlight(fipsMap.get(tile), highlightFeature)}
                on:mousemove={(e) => handleMousemove(e, tile)}
                on:mouseout
                on:blur
                on:mousedown={(e) => handleClick(e, tile)}
              ></path>
            {:else if shape === "rect"}
              <rect
                class="tile-shape"
                width={shapeWidth}
                height={shapeHeight}
                transform="translate({getX(columnIndex, rowIndex, shapeWidth)}, {getY(
                  rowIndex,
                  shapeHeight
                )})"
                fill={getFill(getFeatureData(tile), fill, naFill)}
                stroke="white"
                role="presentation"
                class:highlight={getHighlight(fipsMap.get(tile), highlightFeature)}
                on:mousemove={(e) => handleMousemove(e, tile)}
                on:mouseout
                on:blur
                on:click={(e) => handleClick(e, tile)}
              ></rect>
            {/if}
          {/if}
        {/each}
      {/each}
    </g>
    <g class="map-labels" style:--font-weight="bold">
      {#each mapTiles as row, rowIndex}
        {#each row as tile, columnIndex}
          {#if tile.trim() !== ""}
            <g
              class="tile"
              transform="translate({getX(columnIndex, rowIndex, shapeWidth)}, {getY(
                rowIndex,
                shapeHeight
              )})"
              pointer-events="none"
              role="presentation"
            >
              <text
                class="state-label state-label-stroke"
                x={shapeWidth / 2}
                y={shapeHeight / 2}
                dx="-0.7em"
                dy="0.3em"
                fill="white"
                stroke="white"
                stroke-linejoin="round"
              >
                {tile}
              </text>
              <text
                class="state-label"
                x={shapeWidth / 2}
                y={shapeHeight / 2}
                dx="-0.7em"
                dy="0.3em"
                fill={config.map.fontColor}
              >
                {tile}
              </text>
            </g>
          {/if}
        {/each}
      {/each}
    </g>
  </svg>
</div>

<style>
  .tile-shape {
    cursor: pointer;
  }
  .tile-shape:hover,
  .tile-shape.highlight {
    stroke: var(--hover-stroke);
    stroke-width: var(--hover-stroke-width);
  }
  .hover-fill .tile-shape:hover,
  .hover-fill .tile-shape.highlight {
    fill: var(--hover-fill);
  }
  .tile-shape:focus {
    outline: none;
  }
  .tile text {
    font-weight: var(--font-weight);
  }
  text.state-label {
    font-size: clamp(10px, 1.5vw, var(--font-size-large));
  }
  .state-label-stroke {
    stroke-width: 2;
  }
  @media (min-width: 768px) {
    .state-label-stroke {
      stroke-width: 3;
    }
  }
</style>
